/**
 * Supabase-backed data layer. Tasks and users persist across refresh.
 * Maps DB snake_case <-> app camelCase.
 */
import { supabase } from '../config/supabase';
import { TASK_STATUSES, ADMIN_VERDICTS, isTaskOnTime } from './mockData';

export { TASK_STATUSES, ADMIN_VERDICTS, isTaskOnTime };

function taskRowToApp(row) {
  if (!row) return null;
  return {
    id: row.id,
    taskName: row.task_name,
    clientName: row.client_name,
    projectName: row.project_name,
    estimatedTime: row.estimated_time,
    taskDescription: row.task_description,
    assignedMember: row.assigned_member,
    userId: row.user_id,
    userName: row.user_name ?? row.assigned_member,
    plannedStartDate: row.planned_start_date,
    plannedEndDate: row.planned_end_date,
    currentStatus: row.current_status,
    delayReason: row.delay_reason,
    actualTime: row.actual_time,
    completionDate: row.completion_date,
    adminRemarks: row.admin_remarks,
    adminVerdict: row.admin_verdict,
    // keep snake_case for compatibility where used
    task: row.task_name,
    project: row.project_name,
    status: row.current_status,
    date: row.planned_start_date ?? row.planned_end_date
  };
}

/** Full row for INSERT — required fields get fallbacks */
function taskAppToRow(data) {
  const row = {};
  row.task_name = data.taskName != null && data.taskName !== '' ? String(data.taskName) : '';
  row.client_name = data.clientName != null && data.clientName !== '' ? String(data.clientName) : '';
  row.project_name = data.projectName != null && data.projectName !== '' ? String(data.projectName) : '';
  row.assigned_member = data.assignedMember != null && data.assignedMember !== '' ? String(data.assignedMember) : (data.userName != null ? String(data.userName) : '');
  row.current_status = data.currentStatus != null && data.currentStatus !== '' ? String(data.currentStatus) : 'To Do';
  if (data.estimatedTime != null && data.estimatedTime !== '') row.estimated_time = Number(data.estimatedTime);
  else if (data.estimatedTime === 0) row.estimated_time = 0;
  if (data.taskDescription != null) row.task_description = data.taskDescription;
  if (data.userId != null && data.userId > 0) row.user_id = Number(data.userId);
  if (data.userName != null) row.user_name = data.userName;
  if (data.plannedStartDate != null && data.plannedStartDate !== '') row.planned_start_date = data.plannedStartDate;
  if (data.plannedEndDate != null && data.plannedEndDate !== '') row.planned_end_date = data.plannedEndDate;
  if (data.delayReason != null) row.delay_reason = data.delayReason;
  if (data.actualTime != null && data.actualTime !== '') row.actual_time = Number(data.actualTime);
  if (data.completionDate != null && data.completionDate !== '') row.completion_date = data.completionDate;
  if (data.adminRemarks != null) row.admin_remarks = data.adminRemarks;
  if (data.adminVerdict != null) row.admin_verdict = data.adminVerdict;
  return row;
}

/** Partial row for UPDATE — only fields present in data are set. Skip empty dates and NaN numbers. */
function taskAppToRowPartial(data) {
  const row = {};
  if (data.taskName != null) row.task_name = String(data.taskName);
  if (data.clientName != null) row.client_name = String(data.clientName);
  if (data.projectName != null) row.project_name = String(data.projectName);
  if (data.currentStatus != null) row.current_status = String(data.currentStatus);
  if (data.assignedMember != null) row.assigned_member = String(data.assignedMember);
  if (data.userName != null) row.user_name = String(data.userName);
  if (data.taskDescription != null) row.task_description = data.taskDescription;
  if (data.delayReason != null) row.delay_reason = data.delayReason;
  if (data.adminRemarks != null) row.admin_remarks = data.adminRemarks;
  if (data.adminVerdict != null) row.admin_verdict = data.adminVerdict;
  const est = Number(data.estimatedTime);
  if (data.estimatedTime != null && data.estimatedTime !== '' && !Number.isNaN(est)) row.estimated_time = est;
  const act = Number(data.actualTime);
  if (data.actualTime != null && data.actualTime !== '' && !Number.isNaN(act)) row.actual_time = act;
  if (data.completionDate != null && String(data.completionDate).trim() !== '') row.completion_date = data.completionDate;
  if (data.plannedStartDate != null && String(data.plannedStartDate).trim() !== '') row.planned_start_date = data.plannedStartDate;
  if (data.plannedEndDate != null && String(data.plannedEndDate).trim() !== '') row.planned_end_date = data.plannedEndDate;
  if (data.userId != null && data.userId > 0) row.user_id = Number(data.userId);
  return row;
}

function userRowToApp(row) {
  if (!row) return null;
  return { id: row.id, name: row.name, role: row.role };
}

export async function getTasks() {
  const { data, error } = await supabase.from('tasks').select('*').order('id', { ascending: false });
  if (error) {
    console.error('getTasks error:', error);
    return [];
  }
  return (data || []).map(taskRowToApp);
}

export async function getUsers() {
  const { data, error } = await supabase.from('users').select('*').order('id', { ascending: true });
  if (error) {
    console.error('getUsers error:', error);
    return [];
  }
  return (data || []).map(userRowToApp);
}

/** Get app user (id, name, role) by auth email for login flow. Case-insensitive email match. */
export async function getAppUserByEmail(email) {
  if (!email || typeof email !== 'string') return null;
  const trimmed = email.trim();
  const { data, error } = await supabase
    .from('users')
    .select('id, name, role')
    .ilike('email', trimmed)
    .maybeSingle();
  if (error) {
    console.error('getAppUserByEmail error:', error);
    return null;
  }
  return data ? userRowToApp(data) : null;
}

export async function getTasksByUserId(userId) {
  const tasks = await getTasks();
  return tasks.filter(t => t.userId === userId);
}

export async function getTasksByUserName(userName) {
  const tasks = await getTasks();
  return tasks.filter(t => (t.assignedMember || t.userName) === userName);
}

export async function addTask(taskData) {
  const row = taskAppToRow({
    ...taskData,
    assignedMember: taskData.assignedMember || taskData.userName,
    userName: taskData.assignedMember || taskData.userName
  });
  // Remove undefined/null so Supabase doesn't send invalid values (omit user_id when 0)
  const cleanRow = Object.fromEntries(
    Object.entries(row).filter(([, v]) => v !== undefined && v !== null)
  );
  const { data, error } = await supabase.from('tasks').insert(cleanRow).select('*').single();
  if (error) {
    console.error('addTask error:', error);
    throw error;
  }
  return taskRowToApp(data);
}

export async function deleteTask(taskId) {
  const { error } = await supabase.from('tasks').delete().eq('id', taskId);
  if (error) {
    console.error('deleteTask error:', error);
    throw error;
  }
}

export async function updateTask(taskId, updatedData) {
  const row = taskAppToRowPartial(updatedData);
  if (updatedData.currentStatus === 'Completed' && !row.completion_date) {
    row.completion_date = (updatedData.completionDate && String(updatedData.completionDate).trim() !== '')
      ? updatedData.completionDate
      : new Date().toISOString().split('T')[0];
  }
  // Remove undefined, null, NaN; allow '' for text. Skip empty string only for date/numeric columns to avoid 400
  const cleanRow = {};
  for (const [k, v] of Object.entries(row)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'number' && Number.isNaN(v)) continue;
    if (['planned_start_date', 'planned_end_date', 'completion_date'].includes(k) && String(v).trim() === '') continue;
    if ((k === 'estimated_time' || k === 'actual_time') && (v === '' || Number.isNaN(Number(v)))) continue;
    cleanRow[k] = v;
  }
  if (Object.keys(cleanRow).length === 0) {
    const { data: existing } = await supabase.from('tasks').select('*').eq('id', taskId).single();
    return taskRowToApp(existing);
  }
  const { data, error } = await supabase.from('tasks').update(cleanRow).eq('id', taskId).select('*').single();
  if (error) {
    console.error('updateTask error:', error);
    throw error;
  }
  return taskRowToApp(data);
}
